import { html, css } from '@design.estate/dees-element';
import type { IExecutionEnvironment } from '../../00group-runtime/index.js';
import './dees-workspace-bottombar.js';

/**
 * Minimal mock execution environment so the bottombar's script runner and
 * package checker render realistically in the catalog without booting a real
 * WebContainer. Only the methods the bottombar calls do anything meaningful;
 * the rest are inert stubs that satisfy the interface.
 */
const createMockEnvironment = (config: {
  scripts?: Record<string, string>;
  outdated?: Record<string, unknown>;
  hasPackageJson?: boolean;
}): IExecutionEnvironment => {
  const hasPackageJson = config.hasPackageJson ?? true;
  const packageJson = {
    name: 'demo-project',
    version: '1.0.0',
    scripts: config.scripts ?? {},
  };

  return {
    type: 'mock',
    ready: true,
    async init() {},
    async destroy() {},
    async readFile(path: string) {
      if (path === '/package.json' && hasPackageJson) {
        return JSON.stringify(packageJson, null, 2);
      }
      throw new Error(`mock: file not found: ${path}`);
    },
    async writeFile() {},
    async readDir() {
      return [];
    },
    async mkdir() {},
    async rm() {},
    async exists(path: string) {
      return path === '/package.json' ? hasPackageJson : false;
    },
    watch() {
      return { stop() {} };
    },
    async spawn(command: string, args: string[] = []) {
      const isOutdated = command === 'pnpm' && args[0] === 'outdated';
      const payload = isOutdated && config.outdated ? JSON.stringify(config.outdated) : '';
      const hasUpdates = isOutdated && !!config.outdated && Object.keys(config.outdated).length > 0;
      return {
        output: new ReadableStream<string>({
          start(controller) {
            if (payload) controller.enqueue(payload);
            controller.close();
          },
        }),
        input: { getWriter: () => new WritableStream<string>().getWriter() },
        // pnpm outdated exits 1 when updates exist, 0 otherwise.
        exit: Promise.resolve(hasUpdates ? 1 : 0),
        kill() {},
      };
    },
  };
};

const updatesEnvironment = createMockEnvironment({
  scripts: {
    build: 'tsbuild tsfolders --allowimplicitany && tsbundle',
    watch: 'tswatch element',
    test: 'tstest test/ --verbose',
    format: 'gitzone format',
  },
  outdated: {
    '@design.estate/dees-element': {
      current: '2.2.0',
      wanted: '2.3.0',
      latest: '2.3.0',
      dependencyType: 'dependencies',
    },
    '@git.zone/tsbuild': {
      current: '2.1.0',
      wanted: '2.1.6',
      latest: '2.2.0',
      dependencyType: 'devDependencies',
    },
  },
});

const upToDateEnvironment = createMockEnvironment({
  scripts: { build: 'tsbuild', test: 'tstest test/' },
  outdated: {},
});

export const demoFunc = () => html`
  <style>
    ${css`
      .demo-container {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 24px;
        max-width: 900px;
        margin: 0 auto;
      }

      .ws-frame {
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-lg);
        overflow: hidden;
        box-shadow: var(--dees-shadow-sm);
      }

      .ws-body {
        height: 160px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--dees-color-code-block-bg);
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-footnote-size);
        font-family: var(--dees-font-family-mono);
      }
    `}
  </style>

  <div class="demo-container">
    <dees-panel
      .heading=${'Updates available'}
      .description=${'Script runner (click “Scripts”) plus a package checker reporting outdated dependencies (click “2 updates”). Backed by a mock execution environment.'}
    >
      <div class="ws-frame">
        <div class="ws-body">editor / terminal area</div>
        <dees-workspace-bottombar .executionEnvironment=${updatesEnvironment}></dees-workspace-bottombar>
      </div>
    </dees-panel>

    <dees-panel
      .heading=${'Up to date'}
      .description=${'All dependencies current — the package widget shows the success state.'}
    >
      <div class="ws-frame">
        <div class="ws-body">editor / terminal area</div>
        <dees-workspace-bottombar .executionEnvironment=${upToDateEnvironment}></dees-workspace-bottombar>
      </div>
    </dees-panel>

    <dees-panel
      .heading=${'Idle (no environment)'}
      .description=${'Without an execution environment the widgets fall back to their neutral idle state.'}
    >
      <div class="ws-frame">
        <div class="ws-body">editor / terminal area</div>
        <dees-workspace-bottombar></dees-workspace-bottombar>
      </div>
    </dees-panel>
  </div>
`;
